import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { TranslatedPropDTO } from "../../dto/TranslatedPropDTO";
import { PropService } from "../../services/PropService";
import { SituationService } from "../../services/SituationService";
import { PropLibraryScope } from "./PropLibraryScopeSwitcher";

type LoadDescription = (prop: TranslatedPropDTO) => Promise<string | null>;
const DescriptionContext = createContext<LoadDescription | null>(null);

/** Only mounted (visible) cards request metadata. Share requests with the details
 * pane and cap concurrency; this must never gate selection or adding a Prop. */
export function PropLibraryDescriptionProvider({ scope, children }: {
  scope: PropLibraryScope;
  children: ReactNode;
}) {
  const orgId = useSelector(selectCurrentOrganization)?.id;
  const token = useSelector(selectToken);
  const load = useMemo<LoadDescription>(() => {
    const cache = new Map<string, Promise<string | null>>();
    const queue: Array<() => void> = [];
    let active = 0;
    const runNext = () => {
      while (active < 4 && queue.length) {
        active++;
        queue.shift()!();
      }
    };
    return (prop) => {
      if (!orgId || !token) return Promise.resolve(null);
      const key = `${prop.id}:${prop.version}`;
      const cached = cache.get(key);
      if (cached) return cached;
      const request = new Promise<string | null>((resolve) => {
        queue.push(() => {
          const fetchDescription = async () => {
            const content = scope === "props"
              ? await PropService.GetPropContent(orgId, token, prop.id)
              : await SituationService.GetSituationsContent(orgId, token, prop.id);
            if (!content) return null;
            // Version zero has no uploaded metadata yet.
            if (!content.metadataUri) return prop.version === 0 ? "" : null;
            const metadata = await PropService.GetPropMetadata(content.metadataUri);
            return metadata ? metadata.description?.trim() ?? "" : null;
          };
          fetchDescription().then(resolve, () => resolve(null)).finally(() => {
            active--;
            runNext();
          });
        });
      });
      cache.set(key, request);
      runNext();
      return request;
    };
  }, [orgId, token, scope]);
  return <DescriptionContext.Provider value={orgId && token ? load : null}>{children}</DescriptionContext.Provider>;
}

export default function PropLibraryDescription({ prop, className, lines }: {
  prop: TranslatedPropDTO;
  className?: string;
  lines?: number;
}) {
  const load = useContext(DescriptionContext);
  const { t } = useTranslation();
  const key = `${prop.id}:${prop.version}`;
  const [result, setResult] = useState<{ key: string; load: LoadDescription; text: string | null }>();
  useEffect(() => {
    if (!load) return;
    let mounted = true;
    load(prop).then((text) => {
      if (mounted) setResult({ key, load, text });
    });
    return () => { mounted = false; };
  }, [load, key, prop]);
  const ready = result?.key === key && result.load === load;
  return (
    <span className={className} style={lines ? {
      display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: lines, overflow: "hidden",
    } : { whiteSpace: "pre-wrap" }}>
      {!load ? <em>{t("prop_library_ui.description_unavailable")}</em> : !ready ? (
        <span aria-label={t("prop_library_ui.loading_description") ?? ""} className="block h-[14px] w-3/4 rounded bg-creator-childActive animate-pulse" />
      ) : result.text ? result.text : (
        <em>{t(result.text === null ? "prop_library_ui.description_unavailable" : "prop_library_ui.no_description")}</em>
      )}
    </span>
  );
}
